<script setup lang="ts">
definePageMeta({ layout: 'marketing' })

import { BLOG_POSTS, VERTICAL_LABELS, type BlogPostMeta } from '~/utils/blogPosts'

useSeoMeta({
  title: 'Caseflow blog — practical guides for small services agencies',
  description: 'Hands-on guides for wedding planners, funeral directors, tutoring centres, talent agencies, au pair agencies, and town planners.',
  ogTitle: 'Caseflow blog — practical guides for small services agencies',
  ogDescription: 'Hands-on guides for wedding planners, funeral directors, tutoring centres, talent agencies, au pair agencies, and town planners.',
  ogImage: '/og-image.png',
  twitterCard: 'summary_large_image',
  twitterImage: '/og-image.png',
})

// Tells Google this index is a Blog whose entries are the manifest posts.
useStructuredData({
  '@type': 'Blog',
  name: 'Caseflow blog',
  description: 'Practical guides for small services agencies',
  blogPost: BLOG_POSTS.map(p => ({
    '@type': 'BlogPosting',
    headline: p.title,
    url: `/blog/${p.slug}`,
    datePublished: p.published_at,
    author: { '@type': 'Organization', name: p.author },
  })),
})

const grouped = computed(() => {
  const buckets: Record<string, BlogPostMeta[]> = {}
  for (const post of BLOG_POSTS) {
    if (!buckets[post.vertical]) buckets[post.vertical] = []
    buckets[post.vertical].push(post)
  }
  return buckets
})

const fmtDate = (s: string) => new Date(s).toLocaleDateString('en-AU', { year: 'numeric', month: 'short', day: 'numeric' })
</script>

<template>
  <section class="mx-auto max-w-5xl px-6 pt-20 pb-12">
    <p class="text-xs uppercase tracking-wider font-medium text-indigo-600">Blog</p>
    <h1 class="mt-3 text-4xl md:text-5xl font-bold tracking-tight text-gray-900">Practical guides for small services agencies.</h1>
    <p class="mt-4 text-gray-600 max-w-2xl">Hands-on advice for running a tighter agency. Written for the vertical you actually work in.</p>
  </section>

  <section class="mx-auto max-w-5xl px-6 pb-20 space-y-14">
    <div v-for="(posts, vertical) in grouped" :key="vertical">
      <header class="flex items-baseline justify-between flex-wrap gap-2">
        <h2 class="text-2xl font-semibold text-gray-900">{{ VERTICAL_LABELS[vertical as keyof typeof VERTICAL_LABELS] }}</h2>
        <NuxtLink :to="`/${vertical}`" class="text-sm text-indigo-600 hover:underline">See the {{ VERTICAL_LABELS[vertical as keyof typeof VERTICAL_LABELS].toLowerCase() }} page →</NuxtLink>
      </header>

      <div class="mt-6 grid md:grid-cols-2 gap-5">
        <NuxtLink
          v-for="post in posts" :key="post.slug"
          :to="`/blog/${post.slug}`"
          class="block rounded-xl border border-gray-200 bg-white p-6 hover:shadow-md transition group"
        >
          <div :class="['h-1 w-12 rounded mb-4 bg-gradient-to-r', post.accent_from, post.accent_to]"></div>
          <h3 class="text-lg font-semibold text-gray-900 group-hover:text-indigo-700">{{ post.title }}</h3>
          <p class="mt-2 text-sm text-gray-600">{{ post.excerpt }}</p>
          <p class="mt-4 text-xs text-gray-500">{{ fmtDate(post.published_at) }} · {{ post.read_minutes }} min read</p>
        </NuxtLink>
      </div>
    </div>
  </section>
</template>
